import type { ItemPriceSheet } from '@erp/shared';

import type { XlsxColumn, XlsxValue } from '../../lib/xlsxExport.js';

/** The words the sheet is written in — the page hands them over translated. */
export interface PriceSheetLabels {
  code: string;
  name: string;
  unit: string;
  pieces: string;
  barcode: string;
  normal: string;
  /** The purchase price kept on the product card. */
  purchase: string;
  /** What the newest posted purchase bill paid. */
  lastPurchase: string;
  /** "Quantity" — a column per warehouse is "Quantity · <warehouse>". */
  qty: string;
  /** The all-warehouses quantity column. */
  qtyTotal: string;
  category: string;
  brand: string;
  status: string;
  store: string;
  otherBarcodes: string;
  /** Tagged onto a hidden pack's name: "كرتون (مخفية)". */
  hiddenUnit: string;
  active: string;
  inactive: string;
  visible: string;
  hidden: string;
}

/** The currency most of these prices are in — the one a column's header names. */
export function mainCurrency(codes: Array<string | null | undefined>): string | null {
  const tally = new Map<string, number>();
  for (const c of codes) if (c) tally.set(c, (tally.get(c) ?? 0) + 1);
  let best: string | null = null;
  let most = 0;
  for (const [c, k] of tally) if (k > most) { best = c; most = k; }
  return best;
}

const priced = (amount: string | null | undefined, currency: string | null | undefined): XlsxValue => {
  if (!amount) return null;
  return currency ? { money: Number(amount), currency } : Number(amount);
};

const named = (label: string, ccy: string | null | undefined): string => (ccy ? `${label} · ${ccy}` : label);

/**
 * On hand in the ROW's unit: pieces on the piece row, the same stock in packs on
 * a pack row (120 pieces → 10 on a carton of 12; a part pack shows as 10.42).
 * One cell per warehouse, and the total when there is more than one.
 */
function stockCells(warehouses: number, onHand: string[] | undefined, factor: number): XlsxValue[] {
  if (warehouses === 0) return [];
  const f = Number.isFinite(factor) && factor > 0 ? factor : 1;
  const inUnit = (pieces: number): number => (f === 1 ? pieces : Math.round((pieces / f) * 100) / 100);
  const pieces = Array.from({ length: warehouses }, (_, i) => Number(onHand?.[i] ?? 0) || 0);
  const cells: XlsxValue[] = pieces.map(inUnit);
  if (warehouses > 1) cells.push(inUnit(pieces.reduce((a, b) => a + b, 0)));
  return cells;
}

/**
 * The "Export prices" table: one row per product per unit — the piece, then
 * each pack — ordered by code. Each price column names its currency in the
 * header (the one most of its prices are in); an odd price carries its own.
 * Product-wide facts (category, brand, status) repeat on every unit row so a
 * filter in Excel keeps a product's units together; `band` shades by product.
 */
export function priceSheetTable(
  sheet: ItemPriceSheet,
  labels: PriceSheetLabels,
  names: { category: (id: string) => string | undefined; brand: (id: string) => string | undefined },
  keep?: (itemId: string) => boolean,
): { products: number; columns: XlsxColumn[]; rows: XlsxValue[][]; band: (rowIndex: number) => boolean } {
  const products = sheet.items
    .filter((it) => !keep || keep(it.id))
    .sort((a, b) => a.code.localeCompare(b.code, undefined, { numeric: true }) || a.name.localeCompare(b.name));
  const normalCcy = mainCurrency(products.map((it) => it.saleCurrencyCode));
  const costCcy = sheet.withCost ? mainCurrency(products.map((it) => it.purchaseCurrencyCode)) : null;
  const lastCcy = sheet.withCost ? mainCurrency(products.map((it) => it.lastPurchaseCurrencyCode)) : null;
  const whs = sheet.warehouses ?? [];

  const columns: XlsxColumn[] = [
    { header: labels.code, kind: 'code' },
    { header: labels.name },
    { header: labels.unit },
    { header: labels.pieces, kind: 'number' },
    { header: labels.barcode, kind: 'code' },
    { header: named(labels.normal, normalCcy), kind: 'money', unitPrice: true, currency: normalCcy },
    ...sheet.lists.map((l): XlsxColumn => ({ header: named(l.name, l.currencyCode), kind: 'money', unitPrice: true, currency: l.currencyCode })),
    ...(sheet.withCost
      ? [
        { header: named(labels.purchase, costCcy), kind: 'money', unitPrice: true, currency: costCcy } satisfies XlsxColumn,
        { header: named(labels.lastPurchase, lastCcy), kind: 'money', unitPrice: true, currency: lastCcy } satisfies XlsxColumn,
      ]
      : []),
    ...whs.map((w): XlsxColumn => ({ header: `${labels.qty} · ${w.name}`, kind: 'number' })),
    ...(whs.length > 1 ? [{ header: labels.qtyTotal, kind: 'number' } satisfies XlsxColumn] : []),
    { header: labels.category },
    { header: labels.brand },
    { header: labels.status },
    { header: labels.store },
    { header: labels.otherBarcodes, kind: 'code' },
  ];

  const rows: XlsxValue[][] = [];
  const shade: boolean[] = [];
  products.forEach((it, pi) => {
    for (const u of it.units) {
      shade.push(pi % 2 === 1);
      rows.push([
        it.code,
        it.name,
        u.hidden ? `${u.name} (${labels.hiddenUnit})` : u.name,
        Number(u.factor),
        u.barcode,
        priced(u.salePrice, it.saleCurrencyCode),
        ...sheet.lists.map((_, i) => {
          const p = u.prices[i];
          return p ? priced(p.unitPrice, p.currencyCode) : null;
        }),
        ...(sheet.withCost
          ? [priced(u.purchasePrice, it.purchaseCurrencyCode), priced(u.lastPurchasePrice, it.lastPurchaseCurrencyCode)]
          : []),
        ...stockCells(whs.length, it.onHand, Number(u.factor)),
        (it.categoryId && names.category(it.categoryId)) || null,
        (it.brandId && names.brand(it.brandId)) || null,
        it.isActive ? labels.active : labels.inactive,
        it.isHidden ? labels.hidden : labels.visible,
        u.otherBarcodes.join(' · ') || null,
      ]);
    }
  });

  return { products: products.length, columns, rows, band: (i) => shade[i] ?? false };
}
